import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  Unsubscribe,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

/** 2026 (초등) 3D프린팅 활용 직무연수 — 스토리지·Firestore 규칙과 동일한 값 */
export const ELEMENTARY_3D_PRINTING_2026_ID = 'elementary_3d_printing_2026';

/** 2026 (중등) 3D프린팅 활용 직무연수 — 스토리지·Firestore 규칙과 동일한 값 */
export const MIDDLE_SCHOOL_3D_PRINTING_2026_ID = 'middle_3d_printing_2026';

/** 2026 중부 중등 디벗 핵심교원 AI·디지털 리더 연수 — 스토리지·Firestore 규칙과 동일한 값 */
export const CENTRAL_MIDDLE_DEBETH_AI_DIGITAL_LEADER_2026_ID = 'central_middle_debeth_ai_digital_leader_2026';

export interface PublicTrainingUploadDoc {
  id: string;
  trainingId: string;
  downloadUrl: string;
  storagePath: string;
  originalFileName: string;
  nickname?: string;
  createdAt?: { seconds: number; nanoseconds: number } | null;
}

const MAX_BYTES = 10 * 1024 * 1024;

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-가-힣]/g, '_').slice(0, 180) || 'image';
}

export async function uploadPublicTrainingScreenshot(
  trainingId: string,
  file: File,
  nickname?: string
): Promise<void> {
  if (!file.type.startsWith('image/')) {
    throw new Error('이미지 파일만 업로드할 수 있습니다.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('파일 크기는 10MB 이하여야 합니다.');
  }

  const safe = sanitizeFileName(file.name);
  const storagePath = `notes_public_training/${trainingId}/${Date.now()}_${safe}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);

  await addDoc(collection(db, 'publicTrainingUploads'), {
    trainingId,
    storagePath,
    downloadUrl,
    originalFileName: file.name.slice(0, 200),
    nickname: nickname?.trim().slice(0, 40) || null,
    createdAt: serverTimestamp(),
  });
}

export function subscribePublicTrainingUploads(
  trainingId: string,
  onData: (items: PublicTrainingUploadDoc[]) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, 'publicTrainingUploads'),
    where('trainingId', '==', trainingId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snap) => {
      const items: PublicTrainingUploadDoc[] = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          trainingId: data.trainingId,
          downloadUrl: data.downloadUrl,
          storagePath: data.storagePath,
          originalFileName: data.originalFileName,
          nickname: data.nickname,
          createdAt: data.createdAt,
        };
      });
      onData(items);
    },
    (err) => {
      console.error('publicTrainingUploads 구독 오류:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

export async function deletePublicTrainingUpload(item: PublicTrainingUploadDoc): Promise<void> {
  const fileRef = ref(storage, item.storagePath);
  const docRef = doc(db, 'publicTrainingUploads', item.id);

  try {
    await deleteObject(fileRef);
  } catch (error) {
    // 파일이 이미 없는 경우라도 메타데이터 정리를 계속 시도합니다.
    console.warn('스토리지 파일 삭제 중 경고:', error);
  }

  await deleteDoc(docRef);
}
